import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";

import { ApiError, api } from "../api/client";
import { Button, Card, EmptyState, PageHeader, Skeleton } from "../components/ui";
import { isManagerOrAbove } from "../lib/people";
import { useAuth } from "../auth/AuthProvider";
import { downloadBlob } from "../lib/download";
import { formatDate } from "../lib/format";
import type { EmployeeDirectory } from "../api/types";

/**
 * The number of leave requests waiting, as far as one page can tell.
 *
 * The API pages with a cursor and reports no total, so the waiting requests are
 * counted over a page. A full page means there may be more, and the tile says
 * so rather than quietly showing a number that is too low.
 */
const PENDING_LEAVE_PAGE = 100;

/**
 * Workforce reports. A handful of the numbers an owner actually acts on --
 * headcount by branch/department/type/designation, data-quality gaps, and the
 * last few payroll runs -- not a BI surface. When a source is missing the page
 * says so rather than drawing a zero.
 */
export function ReportsPage() {
  const { user } = useAuth();
  const isManager = isManagerOrAbove(user?.role);
  const timezone = user?.account?.timezone;
  const currency = user?.account?.currency ?? "INR";
  const today = useMemo(() => localDate(timezone), [timezone]);

  const employees = useQuery({
    queryKey: ["employee-options"],
    queryFn: ({ signal }) => api.employees(signal),
    staleTime: 5 * 60_000,
  });
  const payroll = useQuery({
    queryKey: ["payroll-runs", "report"],
    queryFn: ({ signal }) => api.payrollRuns({ page_size: 6 }, signal),
    enabled: isManager,
  });
  const pendingLeave = useQuery({
    queryKey: ["leave-requests", "report", "pending"],
    queryFn: ({ signal }) =>
      api.leaveRequests({ status: "pending", page_size: PENDING_LEAVE_PAGE }, signal),
    enabled: isManager,
  });

  const people = employees.data ?? [];
  const active = people.filter((p) => p.status !== "exited");

  const breakdowns = useMemo(
    () =>
      (
        [
          ["By branch", "branch"],
          ["By department", "department"],
          ["By employment type", "employment_type"],
          ["By designation", "designation"],
        ] as Array<[string, keyof EmployeeDirectory]>
      ).map(([title, key]) => ({
        title,
        rows: countsBy(people, key),
      })),
    [people],
  );

  function exportCsv() {
    const lines = [`as_of,${today}`, "section,label,count"];
    for (const { title, rows } of breakdowns) {
      for (const [label, count] of rows) {
        lines.push(`${csv(title)},${csv(label)},${count}`);
      }
    }
    downloadBlob(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" }), `headcount-${today}.csv`);
  }

  return (
    <>
      <PageHeader
        title="Reports"
        subtitle={`Workforce as of ${formatDate(today, timezone)}.`}
        actions={
          <Button onClick={exportCsv} disabled={people.length === 0}>
            <Download size={15} /> Export headcount
          </Button>
        }
      />

      {employees.isError ? (
        <EmptyState
          title="Reports unavailable"
          body={
            employees.error instanceof ApiError
              ? employees.error.message
              : "The employee source could not be loaded."
          }
        />
      ) : (
        <>
          <p className="mb-3 text-xs text-slate-500">
            Source: the employee register{isManager ? " and the last six payroll runs" : ""}. Figures
            cover everyone except exited employees unless the tile says otherwise.
          </p>

          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Employees" value={String(active.length)} pending={employees.isPending} />
            <Stat
              label="Active"
              value={String(people.filter((p) => p.status === "active").length)}
              pending={employees.isPending}
            />
            <Stat
              label="On leave"
              value={String(people.filter((p) => p.status === "on_leave").length)}
              pending={employees.isPending}
            />
            <Stat
              label="Exited"
              value={String(people.filter((p) => p.status === "exited").length)}
              pending={employees.isPending}
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {breakdowns.map(({ title, rows }) => (
              <Breakdown key={title} title={title} rows={rows} loading={employees.isPending} />
            ))}
            <DataQualityCard people={active} loading={employees.isPending} />
          </div>

          {isManager ? (
            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <Card>
                <h2 className="mb-3 text-sm font-semibold text-slate-900">Recent payroll</h2>
                {payroll.isPending ? (
                  <Skeleton className="h-20 w-full" />
                ) : payroll.isError ? (
                  <EmptyState title="Payroll unavailable" body="Could not load payroll runs." />
                ) : (payroll.data?.results ?? []).length === 0 ? (
                  <EmptyState title="No payroll runs" body="Compute a run from the Payroll module." />
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                        <th className="py-2">Run</th>
                        <th className="py-2">Period</th>
                        <th className="py-2 text-right">Net</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(payroll.data?.results ?? []).map((run) => (
                        <tr key={run.id}>
                          <td className="py-2 text-sm text-slate-800">{run.run_number}</td>
                          <td className="py-2 text-sm text-slate-500">
                            {formatDate(run.period_start, timezone ?? undefined)} →{" "}
                            {run.period_end ? formatDate(run.period_end, timezone ?? undefined) : "—"}
                          </td>
                          <td className="py-2 text-right text-sm tabular-nums text-slate-800">
                            {currency}{" "}
                            {Number(run.totals?.net_pay ?? 0).toLocaleString("en-IN")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>

              <Card>
                <h2 className="mb-3 text-sm font-semibold text-slate-900">Action required</h2>
                <ul className="divide-y divide-slate-100">
                  <li className="flex items-center justify-between py-2.5">
                    <span className="text-sm text-slate-600">Pending leave requests</span>
                    <span className="text-sm font-semibold tabular-nums text-slate-900">
                      {pendingLeave.isPending ? "—" : pendingLeaveLabel(pendingLeave.data?.results.length)}
                    </span>
                  </li>
                </ul>
                <p className="mt-3 text-xs text-slate-400">
                  Approve leave from the Leave module; it updates attendance and the roster.
                </p>
              </Card>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}

function DataQualityCard({ people, loading }: { people: EmployeeDirectory[]; loading: boolean }) {
  const gaps = [
    {
      label: "No reporting manager",
      count: people.filter((p) => !p.reporting_to_name && !p.is_account_owner).length,
    },
    { label: "No department", count: people.filter((p) => !p.department).length },
    { label: "No branch", count: people.filter((p) => !p.branch).length },
    { label: "No designation", count: people.filter((p) => !p.designation).length },
  ];
  return (
    <Card>
      <h2 className="mb-3 text-sm font-semibold text-slate-900">Data quality</h2>
      {loading ? (
        <Skeleton className="h-20 w-full" />
      ) : people.length === 0 ? (
        <p className="text-sm text-slate-500">No active employees to check.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {gaps.map((gap) => (
            <li key={gap.label} className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-600">{gap.label}</span>
              <span
                className={`text-sm font-medium tabular-nums ${
                  gap.count > 0 ? "text-amber-600" : "text-slate-400"
                }`}
              >
                {gap.count}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Breakdown({
  title,
  rows,
  loading,
}: {
  title: string;
  rows: Array<[string, number]>;
  loading: boolean;
}) {
  return (
    <Card>
      <h2 className="mb-3 text-sm font-semibold text-slate-900">{title}</h2>
      {loading ? (
        <Skeleton className="h-20 w-full" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500">No active employees.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map(([label, count]) => (
            <li key={label} className="flex items-center justify-between py-2">
              <span className="text-sm capitalize text-slate-600">{label}</span>
              <span className="text-sm font-medium tabular-nums text-slate-900">{count}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Stat({ label, value, pending }: { label: string; value: string; pending: boolean }) {
  return (
    <Card padded={false} className="px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      {pending ? (
        <Skeleton className="mt-1 h-6 w-10" />
      ) : (
        <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{value}</p>
      )}
    </Card>
  );
}

function countsBy(employees: EmployeeDirectory[], key: keyof EmployeeDirectory): Array<[string, number]> {
  const counts = new Map<string, number>();
  for (const employee of employees) {
    if (employee.status === "exited") continue;
    const raw = employee[key];
    const label = typeof raw === "string" && raw ? raw.replace(/_/g, " ") : "Not set";
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]);
}

function csv(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function localDate(timeZone?: string | null): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timeZone ?? undefined,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const map: Record<string, string> = {};
  for (const part of parts) map[part.type] = part.value;
  return `${map.year}-${map.month}-${map.day}`;
}

/** The waiting count, with a "+" when a full page means there may be more. */
function pendingLeaveLabel(count: number | undefined): string {
  const waiting = count ?? 0;
  return waiting >= PENDING_LEAVE_PAGE ? `${PENDING_LEAVE_PAGE}+` : String(waiting);
}
